(() => {
  if (customElements.get('el-reviews-carousel')) return;

  class ElReviewsCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.track = this.querySelector('[data-reviews-track]');
      if (!this.track) return;

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.previousButtons = this.querySelectorAll('[data-reviews-previous]');
      this.nextButtons = this.querySelectorAll('[data-reviews-next]');
      this.currentCounters = this.querySelectorAll('[data-reviews-current]');
      this.totalCounters = this.querySelectorAll('[data-reviews-total]');
      this.dotsContainer = this.querySelector('[data-reviews-dots]');
      this.filter = this.querySelector('[data-reviews-filter]');
      this.emptyState = this.querySelector('[data-reviews-empty]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileMedia = window.matchMedia('(max-width: 749px)');
      this.autoplayTimer = null;
      this.dragging = false;
      this.dragMoved = false;
      this.scrollFrame = null;

      this.previousButtons.forEach((button) => {
        button.addEventListener('click', () => this.move(-1), { signal: this.signal });
      });
      this.nextButtons.forEach((button) => {
        button.addEventListener('click', () => this.move(1), { signal: this.signal });
      });
      if (this.filter) {
        this.filter.addEventListener('change', (event) => this.applyFilter(event.currentTarget.value), { signal: this.signal });
      }

      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal: this.signal });
      this.track.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal: this.signal });
      this.track.addEventListener('pointermove', (event) => this.onPointerMove(event), { signal: this.signal });
      this.track.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal: this.signal });
      this.track.addEventListener('pointercancel', (event) => this.onPointerUp(event), { signal: this.signal });
      this.track.addEventListener('click', (event) => this.preventDraggedClick(event), { capture: true, signal: this.signal });

      this.querySelectorAll('[data-review-toggle]').forEach((button) => {
        button.addEventListener('click', (event) => this.toggleReview(event), { signal: this.signal });
      });
      this.querySelectorAll('[data-review-hotspot-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', (event) => this.toggleHotspotLayer(event), { signal: this.signal });
      });
      this.querySelectorAll('[data-review-hotspot]').forEach((button) => {
        button.addEventListener('click', (event) => this.toggleHotspot(event), { signal: this.signal });
      });

      if (this.dataset.pauseOnHover === 'true') {
        this.addEventListener('mouseenter', () => this.stopAutoplay(), { signal: this.signal });
        this.addEventListener('mouseleave', () => this.startAutoplay(), { signal: this.signal });
      }
      this.addEventListener('focusin', () => this.stopAutoplay(), { signal: this.signal });
      this.addEventListener('focusout', () => this.startAutoplay(), { signal: this.signal });
      this.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal: this.signal });
      this.addEventListener('shopify:section:load', () => this.refresh(), { signal: this.signal });
      document.addEventListener('visibilitychange', () => this.onVisibilityChange(), { signal: this.signal });
      document.addEventListener('click', (event) => this.onDocumentClick(event), { signal: this.signal });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.closeHotspots();
          this.collapseHotspotLayers();
        }
      }, { signal: this.signal });

      this.resizeObserver = new ResizeObserver(() => this.refresh());
      this.resizeObserver.observe(this);
      this.refresh();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.abortController) this.abortController.abort();
      this.initialized = false;
    }

    get slides() {
      return Array.from(this.track.querySelectorAll('.el-reviews__card:not([hidden])'));
    }

    applyFilter(category) {
      const selectedCategory = category || 'all';
      this.track.querySelectorAll('.el-reviews__card').forEach((card) => {
        card.hidden = selectedCategory !== 'all' && card.dataset.reviewCategory !== selectedCategory;
      });
      this.closeHotspots();
      this.collapseHotspotLayers();
      this.track.scrollTo({ left: 0, behavior: 'auto' });
      if (this.emptyState) this.emptyState.hidden = this.slides.length !== 0;
      requestAnimationFrame(() => this.refresh());
    }

    get isMobile() {
      return this.mobileMedia.matches;
    }

    get mode() {
      return this.isMobile ? this.dataset.mobileMode : this.dataset.desktopMode;
    }

    get columns() {
      const value = this.isMobile ? this.dataset.mobileColumns : this.dataset.desktopColumns;
      return Math.max(1, Number.parseInt(value || '1', 10));
    }

    get isCarousel() {
      return this.mode === 'carousel';
    }

    get isOverflowing() {
      return this.isCarousel && this.slides.length > this.columns && this.track.scrollWidth > this.track.clientWidth + 2;
    }

    get stepSize() {
      const first = this.slides[0];
      if (!first) return this.track.clientWidth;
      const styles = window.getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '0');
      return first.getBoundingClientRect().width + gap;
    }

    get maximumScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    get pageCount() {
      if (!this.isOverflowing) return 1;
      return Math.max(1, Math.ceil((this.slides.length - this.columns) / this.columns) + 1);
    }

    get currentPage() {
      if (!this.isOverflowing || this.maximumScroll === 0) return 0;
      const approximate = Math.round(this.track.scrollLeft / (this.stepSize * this.columns));
      return Math.min(this.pageCount - 1, Math.max(0, approximate));
    }

    refresh() {
      const overflowClass = this.isMobile ? 'is-overflowing-mobile' : 'is-overflowing-desktop';
      const otherClass = this.isMobile ? 'is-overflowing-desktop' : 'is-overflowing-mobile';
      this.classList.remove(otherClass);
      this.classList.toggle(overflowClass, this.isOverflowing);
      this.buildDots();
      this.updateControls();
      this.updateTextOverflow();
      this.positionOpenHotspots();
      this.startAutoplay();
    }

    move(direction) {
      if (!this.isOverflowing) return;
      this.closeHotspots();
      const delta = this.stepSize * this.columns;
      let target = this.track.scrollLeft + direction * delta;
      const atStart = this.track.scrollLeft <= 2;
      const atEnd = this.track.scrollLeft >= this.maximumScroll - 2;

      if (this.dataset.loop === 'true') {
        if (direction > 0 && atEnd) target = 0;
        if (direction < 0 && atStart) target = this.maximumScroll;
      }
      else {
        target = Math.min(this.maximumScroll, Math.max(0, target));
      }

      this.track.scrollTo({ left: target, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
      this.restartAutoplay();
    }

    onScroll() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        this.updateControls();
        this.updateDots();
      });
    }

    updateControls() {
      const loop = this.dataset.loop === 'true';
      const atStart = this.track.scrollLeft <= 2;
      const atEnd = this.track.scrollLeft >= this.maximumScroll - 2;
      this.previousButtons.forEach((button) => {
        button.disabled = !this.isOverflowing || (!loop && atStart);
      });
      this.nextButtons.forEach((button) => {
        button.disabled = !this.isOverflowing || (!loop && atEnd);
      });
    }

    buildDots() {
      if (!this.dotsContainer) return;
      const count = this.pageCount;
      if (this.dotsContainer.children.length !== count) {
        this.dotsContainer.replaceChildren();
        for (let index = 0; index < count; index += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'el-reviews__dot';
          dot.setAttribute('aria-label', `Show review page ${index + 1} of ${count}`);
          dot.addEventListener('click', () => this.goToPage(index), { signal: this.signal });
          this.dotsContainer.append(dot);
        }
      }
      this.updateDots();
    }

    updateDots() {
      this.currentCounters.forEach((counter) => {
        counter.textContent = String(this.currentPage + 1);
      });
      this.totalCounters.forEach((counter) => {
        counter.textContent = String(this.pageCount);
      });
      if (!this.dotsContainer) return;
      Array.from(this.dotsContainer.children).forEach((dot, index) => {
        const active = index === this.currentPage;
        dot.classList.toggle('is-active', active);
        dot.toggleAttribute('aria-current', active);
      });
    }

    goToPage(index) {
      const target = Math.min(this.maximumScroll, index * this.stepSize * this.columns);
      this.track.scrollTo({ left: target, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
      this.restartAutoplay();
    }

    onPointerDown(event) {
      if (event.pointerType !== 'mouse' || !this.isOverflowing || event.button !== 0 || event.target.closest('button, video, [data-review-hotspot-card]')) return;
      this.dragging = true;
      this.dragMoved = false;
      this.dragStartX = event.clientX;
      this.dragStartScroll = this.track.scrollLeft;
      this.track.setPointerCapture(event.pointerId);
      this.stopAutoplay();
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      const distance = event.clientX - this.dragStartX;
      if (Math.abs(distance) > 4) this.dragMoved = true;
      this.track.scrollLeft = this.dragStartScroll - distance;
    }

    onPointerUp(event) {
      if (!this.dragging) return;
      this.dragging = false;
      if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
      this.snapToNearestPage();
      this.startAutoplay();
    }

    preventDraggedClick(event) {
      if (!this.dragMoved) return;
      event.preventDefault();
      event.stopPropagation();
      this.dragMoved = false;
    }

    snapToNearestPage() {
      if (!this.isOverflowing) return;
      const page = Math.round(this.track.scrollLeft / (this.stepSize * this.columns));
      this.goToPage(Math.min(this.pageCount - 1, Math.max(0, page)));
    }

    startAutoplay() {
      this.stopAutoplay();
      if (
        this.dataset.autoplay !== 'true' ||
        !this.isOverflowing ||
        this.reducedMotion.matches ||
        document.visibilityState === 'hidden' ||
        this.matches(':hover') ||
        this.contains(document.activeElement)
      ) return;

      const delay = Math.max(3000, Number.parseInt(this.dataset.autoplaySpeed || '5000', 10));
      this.autoplayTimer = setInterval(() => this.move(1), delay);
    }

    stopAutoplay() {
      if (this.autoplayTimer) clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    onVisibilityChange() {
      if (document.visibilityState === 'hidden') this.stopAutoplay();
      else this.startAutoplay();
    }

    updateTextOverflow() {
      this.querySelectorAll('[data-review-text]').forEach((text) => {
        const toggle = text.parentElement.querySelector('[data-review-toggle]');
        if (!toggle || text.classList.contains('is-expanded')) return;
        toggle.hidden = !(text.scrollHeight > text.clientHeight + 1);
      });
    }

    toggleReview(event) {
      const button = event.currentTarget;
      const text = button.parentElement.querySelector('[data-review-text]');
      if (!text) return;
      const expanded = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!expanded));
      text.classList.toggle('is-expanded', !expanded);
      button.querySelector('[data-review-more]').hidden = !expanded;
      button.querySelector('[data-review-less]').hidden = expanded;
      requestAnimationFrame(() => this.refresh());
    }

    toggleHotspotLayer(event) {
      if (this.dragMoved || event.target.closest('[data-review-hotspot], [data-review-hotspot-card]')) return;
      event.stopPropagation();
      const media = event.currentTarget.closest('.el-reviews__media');
      if (!media) return;
      const willOpen = !media.classList.contains('is-hotspot-active');
      this.closeHotspots();
      this.collapseHotspotLayers(media);
      media.classList.toggle('is-hotspot-active', willOpen);
    }

    toggleHotspot(event) {
      event.preventDefault();
      event.stopPropagation();
      const button = event.currentTarget;
      const media = button.closest('.el-reviews__media');
      if (!media) return;
      if (!media.classList.contains('is-hotspot-active')) {
        this.collapseHotspotLayers(media);
        media.classList.add('is-hotspot-active');
      }
      const expanded = button.getAttribute('aria-expanded') === 'true';
      this.closeHotspots();
      if (expanded) return;
      const card = this.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`);
      const container = button.closest('[data-review-hotspot-container]');
      if (!card || !container) return;
      card.hidden = false;
      container.classList.add('is-open');
      button.setAttribute('aria-expanded', 'true');
      this.placeHotspot(button);
    }

    closeHotspots(except) {
      this.querySelectorAll('[data-review-hotspot][aria-expanded="true"]').forEach((button) => {
        if (button === except) return;
        const card = this.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`);
        button.setAttribute('aria-expanded', 'false');
        button.closest('[data-review-hotspot-container]')?.classList.remove('is-open');
        if (card) {
          card.hidden = true;
          delete card.dataset.placement;
        }
      });
    }

    collapseHotspotLayers(exceptMedia) {
      this.querySelectorAll('.el-reviews__media.is-hotspot-active').forEach((media) => {
        if (media !== exceptMedia) media.classList.remove('is-hotspot-active');
      });
    }

    onDocumentClick(event) {
      if (event.target.closest('[data-review-hotspot-container]')) return;
      this.closeHotspots();
      this.collapseHotspotLayers();
    }

    positionOpenHotspots() {
      this.querySelectorAll('[data-review-hotspot][aria-expanded="true"]').forEach((button) => this.placeHotspot(button));
    }

    placeHotspot(button) {
      const card = this.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`);
      const container = button.closest('[data-review-hotspot-container]');
      const media = button.closest('.el-reviews__media');
      if (!card || !container || !media) return;

      const availableWidth = Math.max(120, Math.min(220, media.clientWidth - 24));
      const cardWidth = this.isMobile ? Math.min(170, availableWidth) : availableWidth;
      card.style.setProperty('--el-reviews-hotspot-card-width', `${cardWidth}px`);
      card.style.setProperty('--el-reviews-hotspot-shift-x', '0px');
      card.style.setProperty('--el-reviews-hotspot-shift-y', '0px');
      const requested = container.dataset.side || 'auto';
      const candidates = requested === 'auto'
        ? ['right', 'left', 'bottom', 'top']
        : [...new Set([requested, 'right', 'left', 'bottom', 'top'])];
      const mediaRect = media.getBoundingClientRect();
      let bestPlacement = candidates[0];
      let bestOverflow = Number.POSITIVE_INFINITY;

      candidates.forEach((placement) => {
        card.dataset.placement = placement;
        const rect = card.getBoundingClientRect();
        const overflow =
          Math.max(0, mediaRect.left + 6 - rect.left) +
          Math.max(0, rect.right - mediaRect.right + 6) +
          Math.max(0, mediaRect.top + 6 - rect.top) +
          Math.max(0, rect.bottom - mediaRect.bottom + 6);
        if (overflow < bestOverflow) {
          bestOverflow = overflow;
          bestPlacement = placement;
        }
      });
      card.dataset.placement = bestPlacement;
      const cardRect = card.getBoundingClientRect();
      const inset = 6;
      let shiftX = 0;
      let shiftY = 0;
      if (cardRect.left < mediaRect.left + inset) shiftX = mediaRect.left + inset - cardRect.left;
      else if (cardRect.right > mediaRect.right - inset) shiftX = mediaRect.right - inset - cardRect.right;
      if (cardRect.top < mediaRect.top + inset) shiftY = mediaRect.top + inset - cardRect.top;
      else if (cardRect.bottom > mediaRect.bottom - inset) shiftY = mediaRect.bottom - inset - cardRect.bottom;
      card.style.setProperty('--el-reviews-hotspot-shift-x', `${Math.round(shiftX)}px`);
      card.style.setProperty('--el-reviews-hotspot-shift-y', `${Math.round(shiftY)}px`);
    }

    onBlockSelect(event) {
      const card = event.target.closest('.el-reviews__card');
      if (!card) return;
      this.stopAutoplay();
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  customElements.define('el-reviews-carousel', ElReviewsCarousel);
})();
